import request from '@/utils/request'

/**
 * 获取指定桥梁的设备配置列表
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number, // API响应代码，成功时为200
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     device_code: string,
 *     name: string,
 *     device_type: string,
 *     type: string,
 *     host: string,
 *     port: number,
 *     connection_type: string,
 *     serial_params: string,
 *     enabled: number,
 *     status: string,
 *     bridgeId: number,
 *     latitude: number,
 *     longitude: number,
 *     location: string
 *   }>
 * }>} 设备配置列表
 */
export function getDevicesByBridge(bridgeId) {
  return request({
    url: '/device-config/getDevicesByBridge',
    method: 'get',
    params: { bridgeId }
  })
}

/**
 * 获取指定类型的设备配置列表
 * @param {string} deviceType 设备类型
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     device_code: string,
 *     name: string,
 *     device_type: string,
 *     type: string,
 *     host: string,
 *     port: number,
 *     connection_type: string,
 *     serial_params: string,
 *     enabled: number,
 *     status: string,
 *     bridgeId: number,
 *     latitude: number,
 *     longitude: number,
 *     location: string
 *   }>
 * }>} 设备配置列表
 */
export function getDevicesByType(deviceType) {
  return request({
    url: '/device-config/getDevicesByType',
    method: 'get',
    params: { deviceType }
  })
}

/**
 * 创建设备配置
 * @param {Object} deviceData 设备配置数据
 * @param {string} deviceData.device_code 设备编码
 * @param {string} deviceData.name 设备名称
 * @param {string} deviceData.device_type 设备类型
 * @param {string} deviceData.host 设备主机地址
 * @param {number} deviceData.port 设备端口
 * @param {string} [deviceData.connection_type] 连接类型
 * @param {string} [deviceData.type] 设备子类型
 * @param {string} [deviceData.serial_params] 串口参数(JSON格式)
 * @param {number} [deviceData.enabled=1] 启用状态(1-启用,0-禁用)
 * @param {number} [deviceData.bridgeId] 关联的桥梁ID
 * @param {number} [deviceData.latitude] 纬度
 * @param {number} [deviceData.longitude] 经度
 * @param {string} [deviceData.location] 设备位置描述
 * @param {string} [deviceData.extend_params] 设备扩展参数(JSON格式)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     device_code: string,
 *     name: string,
 *     device_type: string,
 *     type: string,
 *     host: string,
 *     port: number,
 *     connection_type: string,
 *     serial_params: string,
 *     enabled: number,
 *     status: string,
 *     bridgeId: number,
 *     latitude: number,
 *     longitude: number,
 *     location: string,
 *     extend_params: string
 *   }
 * }>} 创建的设备配置
 */
export function createDevice(deviceData) {
  return request({
    url: '/device-config',
    method: 'post',
    data: deviceData
  })
}

/**
 * 更新设备配置
 * @param {number} id 设备ID
 * @param {Object} deviceData 设备配置数据
 * @param {string} deviceData.device_code 设备编码
 * @param {string} deviceData.name 设备名称
 * @param {string} deviceData.device_type 设备类型
 * @param {string} deviceData.host 设备主机地址
 * @param {number} deviceData.port 设备端口
 * @param {string} [deviceData.connection_type] 连接类型
 * @param {string} [deviceData.type] 设备子类型
 * @param {string} [deviceData.serial_params] 串口参数(JSON格式)
 * @param {number} [deviceData.enabled] 启用状态(1-启用,0-禁用)
 * @param {number} [deviceData.bridgeId] 关联的桥梁ID
 * @param {number} [deviceData.latitude] 纬度
 * @param {number} [deviceData.longitude] 经度
 * @param {string} [deviceData.location] 设备位置描述
 * @param {string} [deviceData.extend_params] 设备扩展参数(JSON格式)
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     device_code: string,
 *     name: string,
 *     device_type: string,
 *     type: string,
 *     host: string,
 *     port: number,
 *     connection_type: string,
 *     serial_params: string,
 *     enabled: number,
 *     status: string,
 *     bridgeId: number,
 *     latitude: number,
 *     longitude: number,
 *     location: string,
 *     extend_params: string
 *   }
 * }>} 更新后的设备配置
 */
export function updateDevice(id, deviceData) {
  return request({
    url: `/device-config/${id}`,
    method: 'put',
    data: deviceData
  })
}

/**
 * 删除设备配置
 * @param {number} id 设备ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 删除结果
 */
export function deleteDevice(id) {
  return request({
    url: `/device-config/${id}`,
    method: 'delete'
  })
}

/**
 * 获取指定ID的设备配置信息
 * @param {number} id 设备ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     device_code: string,
 *     name: string,
 *     device_type: string,
 *     type: string,
 *     host: string,
 *     port: number,
 *     connection_type: string,
 *     serial_params: string,
 *     enabled: number,
 *     status: string,
 *     bridgeId: number,
 *     latitude: number,
 *     longitude: number,
 *     location: string,
 *     extend_params: string
 *   }
 * }>} 设备配置信息
 */
export function getDeviceById(id) {
  return request({
    url: `/device-config/${id}`,
    method: 'get'
  })
}

/**
 * 更新设备状态
 * @param {number} id 设备ID
 * @param {string} status 状态，如：online-在线，offline-离线
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 更新结果
 */
export function updateDeviceStatus(id, status) {
  return request({
    url: `/device-config/${id}/status`,
    method: 'put',
    params: { status }
  })
}

/**
 * 获取指定桥梁的所有设备状态（轻量级）
 * 供前端定时调用，返回设备ID、在线/离线状态、可用/禁用状态
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number, // 设备ID
 *     status: string, // 设备在线/离线状态: "online"|"offline"|"fault"|"unknown"
 *     enabled: number // 设备启用/禁用状态，1:启用 0:禁用
 *   }>
 * }>} 设备状态列表
 */
export function getDeviceStatusByBridge(bridgeId) {
  return request({
    url: '/device-config/getDeviceStatusByBridge',
    method: 'get',
    params: { bridgeId }
  })
}

// 导出API函数集合
export default {
  getDevicesByBridge,
  getDevicesByType,
  getDeviceById,
  createDevice,
  updateDevice,
  deleteDevice,
  updateDeviceStatus,
  getDeviceStatusByBridge
} 